import React, { useEffect, useMemo, useState } from 'react';
import { Table, Form } from 'react-bootstrap';
import Select from 'react-select';
import { postRequestOptions, getRequestOptions } from '../helpers/Fetchwrapper';
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatReportQuantity } from '../helpers/GlobalHelper';
import { useLocation } from 'react-router-dom';

interface ProductCategory {
    id_product_categories: number;
    category_name: string;
    category_code: string;
}

interface AreaWiseProductSalesRow {
    sl_no: number;
    employee_name: string;
    area: string;
    categories: { [categoryId: string]: number };
    is_supervisor_total: boolean;
}

interface AreaWiseProductSalesData {
    categories: ProductCategory[];
    rows: AreaWiseProductSalesRow[];
    period_label: string;
}

const supervisorRowStyle: React.CSSProperties = {
    backgroundColor: '#dbeafe',
};

const stickyHeaderStyle: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 1022,
    backgroundColor: '#f8f9fa',
    boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.4)',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
};

interface EmployeeGroup {
    employees: AreaWiseProductSalesRow[];
    supervisor: AreaWiseProductSalesRow | null;
}

const parseEmployeeGroups = (rows: AreaWiseProductSalesRow[]): EmployeeGroup[] => {
    const groups: EmployeeGroup[] = [];
    let currentGroup: EmployeeGroup = { employees: [], supervisor: null };

    rows.forEach((row) => {
        if (row.is_supervisor_total) {
            currentGroup.supervisor = row;
            groups.push(currentGroup);
            currentGroup = { employees: [], supervisor: null };
        } else {
            currentGroup.employees.push(row);
        }
    });

    if (currentGroup.employees.length > 0) {
        groups.push(currentGroup);
    }

    return groups;
};

const buildSupervisorTotalRow = (
    supervisorRow: AreaWiseProductSalesRow,
    employees: AreaWiseProductSalesRow[]
): AreaWiseProductSalesRow => {
    const categories: { [categoryId: string]: number } = {};

    employees.forEach((employee) => {
        Object.entries(employee.categories).forEach(([categoryId, quantity]) => {
            categories[categoryId] = (categories[categoryId] ?? 0) + quantity;
        });
    });

    return {
        ...supervisorRow,
        categories: Object.fromEntries(
            Object.entries(categories).map(([categoryId, quantity]) => [categoryId, Math.round(quantity * 100) / 100])
        ),
    };
};

const filterReportRows = (
    rows: AreaWiseProductSalesRow[],
    employeeSearch: string
): AreaWiseProductSalesRow[] => {
    const search = employeeSearch.trim().toLowerCase();

    if (!search) {
        return rows;
    }

    const filteredRows: AreaWiseProductSalesRow[] = [];

    parseEmployeeGroups(rows).forEach((group) => {
        const matchingEmployees = group.employees.filter((employee) =>
            employee.employee_name.toLowerCase().includes(search)
        );

        if (matchingEmployees.length > 0) {
            matchingEmployees.forEach((employee) => filteredRows.push(employee));
            if (group.supervisor) {
                filteredRows.push(buildSupervisorTotalRow(group.supervisor, matchingEmployees));
            }
            return;
        }

        if (group.supervisor && group.supervisor.employee_name.toLowerCase().includes(search)) {
            group.employees.forEach((employee) => filteredRows.push(employee));
            filteredRows.push(group.supervisor);
        }
    });

    return filteredRows;
};

const AreaWiseProductSalesReport: React.FC = () => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState<AreaWiseProductSalesData | null>(null);
    const [employeeSearch, setEmployeeSearch] = useState('');
    const [projects, setProjects] = useState<{ label: string; value: string }[]>([]);
    const [project, setProject] = useState({ label: 'Select Project', value: '' });
    const [formData, setFormData] = useState({
        idProjects: query.get('idProjects') ?? '',
        fromDate: query.get('fromDate') ?? '',
        toDate: query.get('toDate') ?? '',
    });

    const filteredRows = useMemo(() => {
        if (!reportData) {
            return [];
        }

        return filterReportRows(reportData.rows, employeeSearch);
    }, [reportData, employeeSearch]);

    const visibleCategories = useMemo(() => {
        if (!reportData) {
            return [];
        }

        return reportData.categories.filter((category) => {
            const categoryKey = String(category.id_product_categories);
            return filteredRows.some((row) => (row.categories[categoryKey] ?? 0) !== 0);
        });
    }, [reportData, filteredRows]);

    useEffect(() => {
        getProjects();
    }, []);

    useEffect(() => {
        if (projects.length > 0) {
            const projectObj = Number(query.get('idProjects'))
                ? projects.find((item) => item.value === Number(query.get('idProjects')))
                : projects[0];

            setProject({
                label: projectObj?.label ?? 'Select Project',
                value: projectObj?.value ?? '',
            });
            setFormData((prev) => ({
                ...prev,
                idProjects: projectObj ? String(projectObj.value) : '',
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]);

    const getProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const projectOptions = resp.data.map((item: any) => ({
                    label: item.name,
                    value: item.id,
                }));
                setProjects(projectOptions);
            })
            .catch((error) => console.error('Error fetching projects:', error));
    };

    const handleProjectChange = (selected: any) => {
        setProject(selected);
        setFormData((prev) => ({
            ...prev,
            idProjects: selected ? String(selected.value) : '',
        }));
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleViewReport = async () => {
        if (!formData.idProjects) {
            toast.error('Please select a project', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (!formData.fromDate || !formData.toDate) {
            toast.error('Please select both From Date and To Date', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (formData.fromDate > formData.toDate) {
            toast.error('To Date must be after or equal to From Date', { position: 'bottom-right', autoClose: 4000 });
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'mis-reports/area-wise-product-sales-report',
                postRequestOptions(formData)
            );
            const data = await response.json();
            if (data.success) {
                setReportData(data.data);
                setEmployeeSearch('');
            } else {
                setReportData(null);
                toast.error(data.message ?? 'Failed to load report', { position: 'bottom-right', autoClose: 4000 });
            }
        } catch (error) {
            console.error('Error submitting data:', error);
            toast.error('Failed to load report', { position: 'bottom-right', autoClose: 4000 });
        } finally {
            setLoading(false);
        }
    };

    const getCategoryLabel = (category: ProductCategory) =>
        category.category_code || category.category_name;

    const getRowTotal = (row: AreaWiseProductSalesRow) =>
        visibleCategories.reduce((sum, category) => {
            const quantity = row.categories[String(category.id_product_categories)] ?? 0;
            return sum + quantity;
        }, 0);

    return (
        <>
            <Card className='mb-4'>
                <Card.Body>
                    <Card.Text>
                        <div className='row'>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project<span className='required'></span></Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="fromDate">
                                    <Form.Label>From Date<span className='required'></span></Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="fromDate"
                                        value={formData.fromDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="toDate">
                                    <Form.Label>To Date<span className='required'></span></Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="toDate"
                                        value={formData.toDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-3'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <button className='btn btn-sm btn-primary p-3' onClick={handleViewReport} disabled={loading}>
                                        {loading && <span className="spinner-border spinner-border-sm"></span>}
                                        <span><i className='fas fa-eye'></i> View Report</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Card.Text>
                </Card.Body>
            </Card>

            <div className='row'>
                <div className='col-12'>
                    {reportData ? (
                        <>
                            <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div>
                                    <strong>Period:</strong> {reportData.period_label}
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <Form.Label htmlFor="employeeSearch" className="mb-0">Search Employee</Form.Label>
                                    <Form.Control
                                        id="employeeSearch"
                                        type="text"
                                        size="sm"
                                        placeholder="Type employee name"
                                        value={employeeSearch}
                                        onChange={(event) => setEmployeeSearch(event.target.value)}
                                        style={{ width: '250px' }}
                                    />
                                </div>
                            </div>
                            <div style={{
                                maxHeight: 'calc(100vh - 300px)',
                                overflowY: 'auto',
                                overflowX: 'auto',
                                position: 'relative',
                            }}>
                            <Table bordered hover size="sm" style={{ marginBottom: 0, width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th style={stickyHeaderStyle}>SL</th>
                                        <th style={stickyHeaderStyle}>Employee Name</th>
                                        <th style={stickyHeaderStyle}>Area</th>
                                        {visibleCategories.map((category) => (
                                            <th
                                                key={category.id_product_categories}
                                                className="text-end"
                                                style={stickyHeaderStyle}
                                            >
                                                {getCategoryLabel(category)}
                                            </th>
                                        ))}
                                        <th className="text-end" style={stickyHeaderStyle}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.map((row, index) => (
                                        <tr
                                            key={`${row.sl_no}-${index}`}
                                            style={row.is_supervisor_total ? supervisorRowStyle : undefined}
                                        >
                                            <td style={row.is_supervisor_total ? supervisorRowStyle : undefined}>{row.sl_no}</td>
                                            <td style={row.is_supervisor_total ? supervisorRowStyle : undefined}>
                                                {row.is_supervisor_total ? <strong>{row.employee_name}</strong> : row.employee_name}
                                            </td>
                                            <td style={row.is_supervisor_total ? supervisorRowStyle : undefined}>{row.area}</td>
                                            {visibleCategories.map((category) => {
                                                const quantity = row.categories[String(category.id_product_categories)] ?? 0;
                                                return (
                                                    <td
                                                        key={category.id_product_categories}
                                                        className="text-end"
                                                        style={row.is_supervisor_total ? supervisorRowStyle : undefined}
                                                    >
                                                        {row.is_supervisor_total ? (
                                                            <strong>{formatReportQuantity(quantity)}</strong>
                                                        ) : (
                                                            formatReportQuantity(quantity)
                                                        )}
                                                    </td>
                                                );
                                            })}
                                            <td
                                                className="text-end"
                                                style={row.is_supervisor_total ? supervisorRowStyle : undefined}
                                            >
                                                {row.is_supervisor_total ? (
                                                    <strong>{formatReportQuantity(getRowTotal(row))}</strong>
                                                ) : (
                                                    formatReportQuantity(getRowTotal(row))
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredRows.length === 0 && (
                                        <tr>
                                            <td colSpan={4 + visibleCategories.length} className="text-center">
                                                {employeeSearch.trim() ? 'No matching employees found' : 'No data available'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                            </div>
                        </>
                    ) : (
                        <div>No data available</div>
                    )}
                </div>
            </div>
        </>
    );
};

export default AreaWiseProductSalesReport;
